import React from "react";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { useAuth } from "context/AuthProvider";
import { RuleRequired } from "utils/MyUtils";
import {
  boolText,
  hasPermission,
  parseBoolean,
  showEmpty,
} from "features/kms/system/SystemPageUtils";

export default function NomorPertandinganManagementPage() {
  const { user } = useAuth();
  const [, , , axiosFetch] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [reloadKey, setReloadKey] = React.useState(0);
  const [caborResp] = useAxios({ method: "GET", url: "/references/cabor", auth: true });
  const [jenisResp] = useAxios({ method: "GET", url: "/references/nomor-jenis", auth: true });
  const [kategoriResp] = useAxios({ method: "GET", url: "/references/nomor-kategori", auth: true });
  const [karakteristikResp] = useAxios({ method: "GET", url: "/references/nomor-karakteristik", auth: true });
  const [penentuResp] = useAxios({ method: "GET", url: "/references/nomor-penentu-keberhasilan", auth: true });

  const groupName = String(user?.groupname || "").toUpperCase();
  const canManage =
    hasPermission(user, "reference.manage") &&
    ["SUPERADMIN", "ADMIN_KONI_PROVINSI", "ADMIN_KONI_KABKOTA"].includes(groupName);
  const canDelete = canManage && !["ADMIN_CABOR", "ADMIN_KLUB"].includes(groupName);
  const isScopedReadOnly = ["ADMIN_CABOR", "ADMIN_KLUB", "PERSONAL"].includes(groupName);
  const caborOptions = Array.isArray(caborResp?.data) ? caborResp.data : [];
  const jenisOptions = Array.isArray(jenisResp?.data) ? jenisResp.data : [];
  const kategoriOptions = Array.isArray(kategoriResp?.data) ? kategoriResp.data : [];
  const karakteristikOptions = Array.isArray(karakteristikResp?.data) ? karakteristikResp.data : [];
  const penentuOptions = Array.isArray(penentuResp?.data) ? penentuResp.data : [];
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];
  const canManageRow = (item) => parseBoolean(item?.can_manage_row);
  const canMarkOfficial = (item) => parseBoolean(item?.can_mark_official);
  const listColumns = [
    "no",
    "cabor_name",
    "nama",
    "nomor_jenis_name",
    "nomor_kategori_name",
    "nomor_karakteristik_name",
    "nomor_penentu_keberhasilan_name",
    "aktif",
    ...(canManage ? ["action"] : []),
  ];

  const coltype = {
    id_nomor_pertandingan: { type: "hidden", name: "id_nomor_pertandingan" },
    id_cabor: {
      label: "Cabor",
      type: "dropdown",
      name: "id_cabor",
      colsize: 12,
      validation: RuleRequired,
      options: caborOptions,
      valueId: "id_cabor",
      labelId: "nama",
      placeholder: "-Pilih Cabor-",
      width: 170,
    },
    cabor_name: {
      label: "Cabor",
      renderFunc: (val) => showEmpty(val),
      width: 120,
    },
    nama: {
      label: "Nomor Pertandingan",
      type: "text",
      name: "nama",
      colsize: 12,
      validation: RuleRequired,
      width: 220,
    },
    id_nomor_jenis: {
      label: "Jenis",
      type: "dropdown",
      name: "id_nomor_jenis",
      colsize: 6,
      validation: RuleRequired,
      options: jenisOptions,
      valueId: "id_nomor_jenis",
      labelId: "nama",
      placeholder: "-Pilih Jenis-",
      width: 100,
    },
    nomor_jenis_name: {
      label: "Jenis",
      renderFunc: (val) => showEmpty(val),
      width: 100,
    },
    id_nomor_kategori: {
      label: "Kategori",
      type: "dropdown",
      name: "id_nomor_kategori",
      colsize: 6,
      validation: RuleRequired,
      options: kategoriOptions,
      valueId: "id_nomor_kategori",
      labelId: "nama",
      placeholder: "-Pilih Kategori-",
      width: 150,
    },
    nomor_kategori_name: {
      label: "Kategori",
      renderFunc: (val) => showEmpty(val),
      width: 110,
    },
    id_nomor_karakteristik: {
      label: "Karakteristik",
      type: "dropdown",
      name: "id_nomor_karakteristik",
      colsize: 6,
      validation: RuleRequired,
      options: karakteristikOptions,
      valueId: "id_nomor_karakteristik",
      labelId: "nama",
      placeholder: "-Pilih Karakteristik-",
      width: 190,
    },
    nomor_karakteristik_name: {
      label: "Karakteristik",
      renderFunc: (val) => showEmpty(val),
      width: 190,
    },
    id_nomor_penentu_keberhasilan: {
      label: "Penentu Keberhasilan",
      type: "dropdown",
      name: "id_nomor_penentu_keberhasilan",
      colsize: 6,
      validation: RuleRequired,
      options: penentuOptions,
      valueId: "id_nomor_penentu_keberhasilan",
      labelId: "nama",
      placeholder: "-Pilih Penentu Keberhasilan-",
      width: 220,
    },
    nomor_penentu_keberhasilan_name: {
      label: "Penentu Keberhasilan",
      renderFunc: (val) => showEmpty(val),
      width: 160,
    },
    aktif: {
      label: "Status",
      type: "checkbox",
      name: "aktif",
      renderFunc: (val) => boolText(val),
      colsize: 3,
      width: 70,
      align: "center",
    },
    action: { label: "Aksi", align: "center", width: 100 },
  };

  const columnsForm = [
    "id_cabor",
    "nama",
    "id_nomor_jenis",
    "id_nomor_kategori",
    "id_nomor_karakteristik",
    "id_nomor_penentu_keberhasilan",
  ];

  const saveHandle = async (form, defaultValue = {}) => {
    const id =
      form?.id_nomor_pertandingan ||
      defaultValue?.id_nomor_pertandingan ||
      "";
    const method = id ? "PUT" : "POST";
    const url = id
      ? `/master/nomor-pertandingan/${id}`
      : "/master/nomor-pertandingan";

    return axiosFetch({
      method,
      url,
      auth: true,
      data: {
        id_cabor: form?.id_cabor || "",
        nama: form?.nama || "",
        id_nomor_jenis: Number(form?.id_nomor_jenis || 0),
        id_nomor_kategori: Number(form?.id_nomor_kategori || 0),
        id_nomor_karakteristik: Number(form?.id_nomor_karakteristik || 0),
        id_nomor_penentu_keberhasilan: Number(
          form?.id_nomor_penentu_keberhasilan || 0,
        ),
      },
    });
  };

  const markOfficialHandle = async (item) => {
    const ok = await confirmMsg(
      "Konfirmasi",
      `Apakah Anda yakin akan meresmikan data ${item?.nama || "ini"}?`,
    );
    if (!ok) return;

    const result = await axiosFetch({
      method: "GET",
      url: `/master/nomor-pertandingan/${item?.id_nomor_pertandingan}/mark-official`,
      auth: true,
    });
    if (result?.data?.success === true) {
      await showMsg("Informasi", "Data berhasil diresmikan.");
      setReloadKey((current) => current + 1);
    } else {
      await showMsg("Error", result?.message || "Gagal meresmikan data.");
    }
  };

  const renderActionExtension = (val, col, item) => {
    if (!canMarkOfficial(item)) {
      return null;
    }
    return (
      <>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            markOfficialHandle(item);
          }}
          className="btn btn-success btn-xs"
          title="Resmikan"
        >
          <i className="fa fa-certificate"></i>
        </a>
        &nbsp;
      </>
    );
  };

  const filters = [
    { dtype: "q", name: "q", colsize: 3 },
    {
      dtype: "dropdown",
      name: "id_cabor",
      label: "Cabor",
      colsize: 3,
      options: caborOptions,
      valueId: "id_cabor",
      labelId: "nama",
      placeholder: "-Semua Cabor-",
    },
    {
      dtype: "dropdown",
      name: "id_nomor_jenis",
      label: "Jenis",
      colsize: 3,
      options: jenisOptions,
      valueId: "id_nomor_jenis",
      labelId: "nama",
      placeholder: "-Semua Jenis-",
    },
    {
      dtype: "dropdown",
      name: "id_nomor_kategori",
      label: "Kategori",
      colsize: 3,
      options: kategoriOptions,
      valueId: "id_nomor_kategori",
      labelId: "nama",
      placeholder: "-Semua Kategori-",
    },
    {
      dtype: "dropdown",
      name: "id_nomor_karakteristik",
      label: "Karakteristik",
      colsize: 3,
      options: karakteristikOptions,
      valueId: "id_nomor_karakteristik",
      labelId: "nama",
      placeholder: "-Semua Karakteristik-",
    },
    {
      dtype: "dropdown",
      name: "id_nomor_penentu_keberhasilan",
      label: "Penentu Keberhasilan",
      colsize: 3,
      options: penentuOptions,
      valueId: "id_nomor_penentu_keberhasilan",
      labelId: "nama",
      placeholder: "-Semua Penentu-",
    },
    !isScopedReadOnly
      ? {
          dtype: "dropdown",
          name: "aktif",
          label: "Status",
          colsize: 3,
          options: statusAktifOptions,
          valueId: "id",
          labelId: "nama",
          placeholder: "-Semua Status-",
        }
      : null,
  ].filter(Boolean);

  return (
    <DataList
      title="Nomor Pertandingan"
      coltype={coltype}
      dlist={{
        url: "/master/nomor-pertandingan",
        columns: listColumns,
        filter: filters,
        sort_by: [
          "cabor_name",
          "nama",
          "nomor_jenis_name",
          "nomor_kategori_name",
          "nomor_karakteristik_name",
        ],
        render_action_extension: renderActionExtension,
      }}
      reload={reloadKey}
      addconfig={
        canManage
          ? {
              columns: columnsForm,
              title: "Tambah Nomor Pertandingan",
              label_tool: "Tambah Nomor Pertandingan",
              defaultValue: {},
              saveHandle,
            }
          : null
      }
      editconfig={
        canManage
          ? {
              columns: ["id_nomor_pertandingan", ...columnsForm],
              title: "Ubah Nomor Pertandingan",
              url: "/master/nomor-pertandingan",
              urlitem: "/master/nomor-pertandingan",
              cellid: "id_nomor_pertandingan",
              isAllow: canManageRow,
              saveHandle,
              normalizeItem: (item) => ({ ...item }),
            }
          : null
      }
      delconfig={
        canDelete
          ? {
              url: "/master/nomor-pertandingan",
              cellid: "id_nomor_pertandingan",
              cellname: "nama",
              isAllow: canManageRow,
            }
          : null
      }
      activateconfig={
        canManage
          ? {
              url: "/master/nomor-pertandingan",
              cellid: "id_nomor_pertandingan",
              cellname: "nama",
              cellactivate: "aktif",
              isAllow: canManageRow,
              labelactivate: "Aktifkan",
              labelinactivate: "Non-aktifkan",
              cfmactivate: "mengaktifkan",
              cfminactivate: "menonaktifkan",
              infoactivate: "diaktifkan",
              infoinactivate: "dinonaktifkan",
            }
          : null
      }
    />
  );
}
